<template>
  <div v-if="releaseSlides?.length" class="release-carousel" data-cy="release-carousel">
    <swiper-container
      ref="swiperEl"
      :slides-per-view="1"
      :speed="500"
      :loop="true"
      :pagination="{
        clickable: true,
      }"
      v-on:swiperslidechange="onSlideChange">
      <swiper-slide v-for="slide in releaseSlides" :key="slide.externalId">
        <div class="image" data-cy="release-image" :style="{ backgroundImage: `url(${slide.imageUrl})` }"></div>
        <div class="text">
          <div class="title" data-cy="release-title">
            {{ slide.title[userLanguage] || slide.title[fallbackLocale] }}
          </div>
          <template v-if="isSubscriptionReleaseSlide(slide)">
            <div v-if="!isSubscriptionClicked">
              <div @click="() => subscribeUser(slide)" data-cy="subscription-prompt" class="text-link">
                {{ $t('newRelease.subscription_prompt') }}
              </div>
            </div>
            <div v-else data-cy="subscription-message" class="title">
              {{ subscriptionMessage }}
            </div>
          </template>
        </div>
      </swiper-slide>
    </swiper-container>
    <div class="footer">
      <div>
        <div v-if="isLastSlide">
          <a class="finish-button" data-cy="release-finish-button">
            <BackstageButton v-on:click="completeNewRelease">
              {{ $t('newRelease.confirm_button_label') }}
            </BackstageButton>
          </a>
        </div>
        <div v-else>
          <button class="nav-button prev-button" data-cy="release-cursor-prev" v-on:click="prev">
            <i class="first-icon-plus" />
          </button>
          <button class="nav-button next-button" data-cy="release-cursor-next" v-on:click="next">
            <i class="second-icon-plus" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import BackstageButton from '@/components/common/BackstageButton.vue';
import { utilsMixins } from '@/mixins/utils.mixins';
import { NotificationCategory } from '@/models/notification.enum';
import { UsersService } from '@/services/users.service';
import { useUserStore } from '@/stores/user';
import type { FeatureReleaseModel } from '@onrunning/backstage-models';
import type { FeatureSlideModel } from '@onrunning/backstage-models/dist/feature-slide.model';
import type { SubscriptionsModel } from '@onrunning/backstage-models/src/subscriptions.model';
import { merge } from 'lodash';
import { storeToRefs } from 'pinia';
import { defineComponent, type PropType } from 'vue';
import { useLocale } from '../../composables/locale';
import { globalConstants } from '../../constants/global.constants';

export default defineComponent({
  name: 'release-carousel',
  components: {
    BackstageButton,
  },
  props: {
    newRelease: {
      required: false,
      type: Object as PropType<FeatureReleaseModel>,
      default: undefined,
    },
  },
  setup() {
    const { userLanguage } = useLocale();
    const { userProfile } = storeToRefs(useUserStore());

    return {
      userLanguage,
      fallbackLocale: globalConstants.fallBackLocale,
      userProfile,
    };
  },
  data() {
    return {
      featureRelease: this.newRelease,
      currentSlideIndex: 0,
      releaseSlides: this.newRelease?.slides,
      userService: new UsersService(),
      isSubscriptionClicked: false,
      subscriptionMessage: '',
    };
  },
  computed: {
    isLastSlide(): boolean {
      return this.releaseSlides?.length ? this.releaseSlides?.length - 1 === this.currentSlideIndex : false;
    },
  },
  methods: {
    ...utilsMixins,
    completeNewRelease() {
      if (this.featureRelease) {
        this.userService.updateRelease(this.featureRelease.externalId);
        this.$emit('completeNewRelease');
      }
    },
    next() {
      (this.$refs.swiperEl as any).swiper.slideNext();
    },
    prev() {
      (this.$refs.swiperEl as any).swiper.slidePrev();
    },
    onSlideChange(event: { detail: { activeIndex: number }[] }) {
      this.currentSlideIndex = event.detail[0].activeIndex;
    },
    isSubscriptionReleaseSlide(slide: FeatureSlideModel): boolean {
      return slide.additionalInformation?.type === 'subscription';
    },
    isShipmentEmailSubscriptionReleaseSlide(slide: FeatureSlideModel): boolean {
      return slide.additionalInformation?.name === NotificationCategory.OrderShipment;
    },
    updateUserSubscriptions(slide: FeatureSlideModel): { subscriptions: SubscriptionsModel } | undefined {
      let notificationSubscription;

      if (this.isShipmentEmailSubscriptionReleaseSlide(slide)) {
        notificationSubscription = { orderShipment: { email: true } };
      }

      return {
        subscriptions: merge({}, this.userProfile?.user.notificationSubscriptions, notificationSubscription),
      } as { subscriptions: SubscriptionsModel };
    },
    async subscribeUser(slide: FeatureSlideModel) {
      this.isSubscriptionClicked = true;
      if (this.userProfile?.user) {
        const userPreferences = this.updateUserSubscriptions(slide);

        if (userPreferences) {
          const result = await this.userService.updatePreferences(userPreferences);
          if (result.isValid()) {
            this.subscriptionMessage = this.$t('newRelease.subscription_successful');

            return;
          }
        } else {
          this.subscriptionMessage = this.$t('newRelease.subscription_failed');
        }
      }
      this.subscriptionMessage = this.$t('newRelease.subscription_failed');
    },
  },
});
</script>

<style scoped lang="scss">
.release-carousel:deep() {
  max-width: 820px;

  swiper-container {
    position: relative;
    box-sizing: border-box;
    user-select: none;
    touch-action: pan-y;
    overflow: hidden;
    height: 100;
    object-fit: contain;
    display: flex;
    align-items: center;
    justify-content: center;
    --swiper-pagination-bottom: 10px;
    --swiper-pagination-color: var(--primary-button-bg-color);

    swiper-slide {
      padding-bottom: 80px;

      .image {
        position: relative;
        width: 100%;
        height: 30vh;
        aspect-ratio: 2 / 1;
        background-repeat: no-repeat;
        background-size: contain;
        background-position: center;

        .finish-button {
          display: none;
          margin-top: 8px;
          max-height: 30px;
        }
      }

      .text {
        color: var(--primary-button-bg-color);
        position: absolute;
        left: 0;
        bottom: 10px;
        max-width: 70%;
        text-align: left;

        .title {
          font-size: 1rem;
        }

        .text-link {
          color: blue;
          text-decoration: underline;
          cursor: pointer;
          padding: 15px;
          margin-left: -15px;
        }
      }
    }
  }

  .footer {
    position: absolute;
    right: 44px;
    bottom: 44px;
    z-index: 2;

    .nav-button {
      width: 42px;
      height: 42px;
      border-radius: 100%;
      border: none;
      background-repeat: no-repeat;
      background-position: center;
      background-color: var(--primary-button-bg-color);
      cursor: pointer;
    }

    .prev-button {
      background-image: url('@/assets/img/chevron-left.svg');
      background-size: 60%;
    }

    .next-button {
      background-image: url('@/assets/img/chevron-right.svg');
      background-size: 25%;
      margin-left: 12px;
    }
  }
}
</style>
